import type { ShellFramePlan, SurfaceDecision } from './kernel-contract.js';

/**
 * CAOS-1517 — re-resolve the shell when the set of installed packages changes.
 *
 * ── THE DEFECT ────────────────────────────────────────────────────────────────────────────────
 * The boot decision (`boot/surface`) is fetched EXACTLY ONCE per document load, in `main.ts`, and
 * the shell frame is built from it. Everything after that is client-side: `clientNavigate` repaints
 * app-scoped regions from the plan already in memory, and no code path re-asks the kernel what the
 * shell should contain. So when a package is uninstalled, the kernel stops naming it in
 * `boot/surface` immediately and every list that re-reads the org agrees it is gone — while the
 * open tab keeps rendering that package's pinned edge anchor until someone reloads the browser.
 *
 * The stale anchor is not cosmetic: it is a live control for software that is no longer installed.
 * Pressing it reaches a surface the org no longer has.
 *
 * ── WHY THIS IS A MODULE AND NOT A FEW LINES IN `main.ts` ─────────────────────────────────────
 * `main.ts` executes on import, wires globals and cannot be imported by a test. The decision of
 * WHETHER the shell changed is the part with the interesting failure modes, so it lives here, pure
 * and directly testable, next to {@link installShellRefreshTriggers} which owns the listeners.
 * `main.ts` keeps only the two calls that connect them.
 *
 * ── THE TWO WAYS THIS COULD BE WORSE THAN THE BUG ─────────────────────────────────────────────
 * Both are guarded here rather than left to the caller:
 *
 *   1. **Re-rendering when nothing changed.** Rebuilding the frame throws away the mounted shell —
 *      scroll, focus, any open panel. A refresh that fires on every navigation and re-renders every
 *      time would be a far worse regression than a stale anchor. So the plan is reduced to a
 *      signature and the re-render happens ONLY when that signature actually differs. A surface
 *      contributed by a package that is still installed produces an identical signature and is
 *      therefore not merely preserved — it is never touched.
 *
 *   2. **Replacing a working workspace with an error page.** {@link fetchKernelDecision} is
 *      fail-soft: it never throws, it returns an `error` decision when the kernel is unreachable or
 *      answers badly. That is right at BOOT, where an error page is the only honest thing to show.
 *      It is wrong on a REFRESH, where a working shell is already on screen: one dropped request
 *      would blank the workspace. So a refresh never renders an `error` decision. It reports
 *      `unreachable` and leaves what is mounted exactly as it is.
 */

/**
 * What one refresh did. Every outcome is a distinct, observable state rather than a boolean — a
 * refresh that found nothing to do and a refresh that could not ask are the same to the user and
 * must not be the same to a test.
 */
export type ShellRefreshOutcome =
  /** The kernel's shell composition is unchanged. Nothing was re-rendered. */
  | 'unchanged'
  /** The composition changed and the caller's renderer was invoked with the new decision. */
  | 'changed'
  /** The kernel could not be asked, or answered with an error. The mounted shell is untouched. */
  | 'unreachable'
  /** A refresh was already in flight; this call did nothing rather than issuing a second fetch. */
  | 'in-flight';

/**
 * A stable string identifying WHAT THE FRAME IS COMPOSED OF — not what it looks like.
 *
 * Deliberately narrow. It covers the surface discriminator, the shell master, the declared
 * surfaces, and every seam's ordered injections by `injectionPoint|sourceKey`. It does NOT cover
 * labels, icons, or attached forms: those are resolved from org metadata (`enrichShellPlan`) and a
 * change to one is not a change to which packages contribute. Widening it would trade this
 * ticket's bug for a frame that rebuilds itself on unrelated edits.
 *
 * Seam keys are sorted so two plans that differ only in key insertion order compare equal; the
 * refs WITHIN a seam are not sorted, because their order is the rendered order (a pinned seam
 * order is a real, user-visible arrangement — see `seamOrderOverrides` on the plan).
 */
export function shellSignature(decision: SurfaceDecision): string {
  if (decision.surface !== 'hand-to-shell') return `surface:${decision.surface}`;
  const shell: ShellFramePlan | undefined = decision.shell;
  if (!shell) return 'hand-to-shell:no-shell';

  const seams = Object.keys(shell.injections)
    .sort()
    .map((seam) => {
      const refs = shell.injections[seam] ?? [];
      const entries = refs.map((ref) => `${ref.injectionPoint}|${ref.sourceKey}`).join(',');
      return `${seam}=[${entries}]`;
    })
    .join(';');

  const order = Object.keys(shell.seamOrderOverrides ?? {})
    .sort()
    .map((seam) => `${seam}=[${(shell.seamOrderOverrides?.[seam] ?? []).join(',')}]`)
    .join(';');

  return [
    'hand-to-shell',
    `master:${shell.master}`,
    `surfaces:[${[...shell.surfaces].sort().join(',')}]`,
    `injections:{${seams}}`,
    `order:{${order}}`,
  ].join(' ');
}

/**
 * The full composition: the kernel plan PLUS the installed apps the host projects into the
 * `navigation-entries` seam itself. See the header for why one half is not enough.
 *
 * App keys are only part of it for `hand-to-shell`, because the nav strip and app switcher exist
 * only inside the shell. An org with apps installed but no workspace renders empty-workspace, and
 * folding its app list into that signature would make every refresh there read as a change.
 *
 * Their ORDER is significant: `enrichShellPlan` maps them straight into the seam in the order the
 * registry returned them, so it is the rendered order of the nav strip.
 */
export function compositionSignature(
  decision: SurfaceDecision,
  appKeys: readonly string[],
): string {
  const base = shellSignature(decision);
  if (decision.surface !== 'hand-to-shell') return base;
  return `${base} apps:[${appKeys.join(',')}]`;
}

/** What a refresher needs from the host. Both are injected so the whole thing is testable. */
export interface ShellRefreshDeps {
  /**
   * Re-ask the kernel. This is `fetchKernelDecision` in production — note that it resolves to an
   * `error` decision rather than rejecting, which is why {@link ShellRefresher.refresh} inspects
   * the decision instead of relying on a thrown exception.
   */
  readonly fetchDecision: () => Promise<SurfaceDecision>;
  /**
   * The installed app keys, in registry order — the half of the composition the kernel decision
   * does not carry. `null` means "could not determine", which is NOT the same as "no apps": the
   * host's `fetchOrgMetadata` is fail-soft and returns empty metadata on a failed request, and
   * treating that as an empty app list would re-render the workspace with an emptied nav strip.
   */
  readonly fetchAppKeys: () => Promise<readonly string[] | null>;
  /** Re-render from a decision. This is `renderProductionDecision` in production. */
  readonly render: (decision: SurfaceDecision) => Promise<void>;
}

export interface ShellRefresher {
  /**
   * Record the composition that is now on screen. Called once after the initial boot render and
   * again after any render this refresher caused, so the next comparison is against what the user
   * is actually looking at rather than against the first decision of the session.
   */
  seed(decision: SurfaceDecision, appKeys: readonly string[]): void;
  /** Re-resolve, and re-render only if the composition changed. Never throws. */
  refresh(): Promise<ShellRefreshOutcome>;
}

/**
 * Build a refresher over the two seams. Holds one piece of state — the signature of what is
 * mounted — and one guard against overlapping fetches.
 */
export function createShellRefresher(deps: ShellRefreshDeps): ShellRefresher {
  let mounted: string | null = null;
  let inFlight = false;

  return {
    seed(decision: SurfaceDecision, appKeys: readonly string[]): void {
      mounted = compositionSignature(decision, appKeys);
    },

    async refresh(): Promise<ShellRefreshOutcome> {
      // Navigation and a visibility change can land in the same tick (opening a link in a
      // backgrounded tab). Two concurrent fetches would race to re-render the frame from two
      // decisions, and the loser would win. One at a time.
      if (inFlight) return 'in-flight';
      // Nothing has been rendered yet, so there is no "changed" to detect and boot is still the
      // thing that will paint. Refreshing here would race the initial render.
      if (mounted === null) return 'unchanged';

      inFlight = true;
      try {
        let next: SurfaceDecision;
        let appKeys: readonly string[] | null;
        try {
          // One round trip, not two — a refresh runs on navigation and must not add latency the
          // user can feel. Both seams are fail-soft in production; neither rejects.
          [next, appKeys] = await Promise.all([deps.fetchDecision(), deps.fetchAppKeys()]);
        } catch {
          // Both production seams are fail-soft, but a caller may inject something that is not.
          return 'unreachable';
        }

        // See the header: an `error` decision on a REFRESH means the question could not be
        // answered, and a working shell is already on screen. Never trade it for an error page.
        if (next.surface === 'error') return 'unreachable';
        // Same rule for the other half — but only where it is actually rendered. Outside the shell
        // there is no nav strip, so an undeterminable app list is not a reason to decline.
        if (appKeys === null && next.surface === 'hand-to-shell') return 'unreachable';

        const signature = compositionSignature(next, appKeys ?? []);
        if (signature === mounted) return 'unchanged';

        // Seed BEFORE rendering. If the render throws, the composition the kernel reported is
        // still the one we last acted on, so the next refresh does not re-render it again in a
        // loop against a renderer that is failing.
        mounted = signature;
        await deps.render(next);
        return 'changed';
      } finally {
        inFlight = false;
      }
    },
  };
}

/** The browser surface {@link installShellRefreshTriggers} needs, narrowed so a test can pass fakes. */
export interface ShellRefreshTriggerTargets {
  readonly window: Pick<Window, 'addEventListener'>;
  readonly document: Pick<Document, 'addEventListener' | 'visibilityState'>;
}

/**
 * Register the moments a refresh is worth making. Returns the number of listeners registered so a
 * caller (and a test) can tell "wired" from "silently wired to nothing".
 *
 * Two triggers, and the reasoning for each:
 *
 *   • **`popstate`** — back/forward repaints the frame in place without a document load
 *     (`main.ts`), so it is a navigation the kernel is never re-asked about. Forward navigation is
 *     handled by `main.ts` calling `refresh()` from `clientNavigate` directly, because that path
 *     fires no event.
 *   • **`visibilitychange` → visible** — this is the one that matters for the reported case. The
 *     uninstall was performed somewhere else entirely (the CLI, another tab, another admin), so no
 *     event occurs in this document at all. Returning to the tab is the moment the user is about to
 *     look at a shell that may be describing software that is gone.
 *
 * Deliberately NOT a poll. A timer would re-ask the kernel forever in every open tab, on a page
 * that changes rarely, to fix a condition that is only visible when someone is looking at it.
 */
export function installShellRefreshTriggers(
  targets: ShellRefreshTriggerTargets,
  refresher: ShellRefresher,
): number {
  targets.window.addEventListener('popstate', () => {
    void refresher.refresh();
  });
  targets.document.addEventListener('visibilitychange', () => {
    if (targets.document.visibilityState === 'visible') void refresher.refresh();
  });
  return 2;
}
